import { Box } from '@mui/material';

import {
  ConfigurationMap,
  Indicator,
  Pie1DData,
  Project,
  TextResult,
  TreemapData,
  TreemapNode,
} from '@arra/core';
import { Pie, PieChart } from 'recharts';
import { User } from '../../../authentication/context/useUser';
import { co2Units, humanizeValue } from '../../../actions/page/ActionCard';
import Markdown from '../../../ui/Markdown';
import co2 from '../images/Empreinte carbonne.svg';
import { ContentLayout, PageLayout, Text } from '../layout';
import { Footer } from './Footer';
import { Header } from './Header';

export const Co2 = ({
  project,
  user,
  configuration,
}: {
  project: Project;
  user: User;
  configuration: ConfigurationMap | undefined;
}) => {
  if (!project || !user || !configuration) {
    return null;
  }

  const co2Config = configuration['pdfResults.config'].co2Page;

  const { totalIndicatorCode, descriptionTextCode, detailedResultCode } =
    co2Config;

  const totalCo2 = project.results.find(
    (result) => (result as Indicator).code === totalIndicatorCode,
  ) as Indicator;

  const co2Description = project.results.find(
    (result) => result.code === descriptionTextCode,
  ) as TextResult;

  const co2Details = project.results.find(
    (result) => detailedResultCode && result.code?.includes(detailedResultCode),
  );

  let co2DetailsData: Pie1DData[] | undefined;

  if (co2Details?.type === 'pie1D') {
    co2DetailsData = co2Details.data;
  } else if (co2Details?.type === 'treemap') {
    co2DetailsData = treemapToPieData(co2Details.data);
  }

  const co2TotalKg =
    co2DetailsData?.reduce((acc, curr) => acc + curr.value, 0) || 0;

  const co2DetailsUnit = co2Config.detailedUnit;
  const totalCo2Humanized =
    totalCo2?.unit && totalCo2?.number
      ? humanizeValue(
          totalCo2.unit,
          Number(totalCo2.number),
          co2Units.has(totalCo2.unit) ? 'co2' : totalCo2.code || '',
        )
      : null;

  co2DetailsData?.map((category) => {
    category.value = Math.round(category.value);
    return category;
  });

  return (
    <PageLayout>
      <Header project={project} pageNumber={1} />
      <ContentLayout color="var(--CAMBRIDGEBLUE)">
        <Box height="30px" />
        <Box display="flex" columnGap={2} alignItems="center">
          <img src={co2} width="80px" alt="Co2 cloud" />
          <Text fontSize="60px" fontWeight="bold">
            Empreinte carbone
          </Text>
        </Box>
        <Box height="30px" />
        <Box
          height="1px"
          width="100%"
          marginBottom={5}
          sx={{
            background:
              'repeating-linear-gradient(90deg,var(--CAMBRIDGEBLUE) 0 20px,white 0 40px)',
          }}
        />
        <Box
          display="flex"
          justifyContent="space-evenly"
          alignItems="center"
          columnGap={5}
        >
          {co2DetailsData ? (
            <Box position="relative">
              <PieChart width={270} height={270}>
                <Pie
                  isAnimationActive={false}
                  data={co2DetailsData}
                  dataKey="value"
                  innerRadius="60%"
                  outerRadius="100%"
                />
              </PieChart>
            </Box>
          ) : null}
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            height="100%"
            alignItems="flex-start"
          >
            <Box display="flex" columnGap={2} alignItems="baseline">
              <Text fontSize="80px" color="black" fontWeight="bold">
                {totalCo2Humanized?.value || (totalCo2 ? totalCo2.number : null)}
              </Text>
              <Text fontSize="25px" color="black" fontWeight="bold">
                {totalCo2Humanized?.unit || totalCo2?.unit}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box height="30px" />
        {co2Description?.text ? (
          <Box display="flex" width="100%" mt={1} pr={10} justifyContent="flex-start">
            <Text fontSize="12px" textAlign="left" style={{ width: '100%' }}>
              <Markdown>{co2Description.text}</Markdown>
            </Text>
          </Box>
        ) : null}
        <Box height="30px" />
        <Box
          display="grid"
          width="100%"
          gridTemplateColumns="50% 50%"
          rowGap={2}
          flexWrap="wrap"
        >
          {co2DetailsData
            ? co2DetailsData.map((data, index) => (
                (() => {
                  const humanizedCategoryValue = humanizeValue(
                    co2DetailsUnit,
                    Number(data.value),
                    co2Units.has(co2DetailsUnit) ? 'co2' : '',
                  );

                  return (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  rowGap={2}
                  columnGap={3}
                >
                  <Box
                    width="20px"
                    height="20px"
                    borderRadius="50%"
                    bgcolor={data.fill}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={1}
                    paddingRight={1}
                  >
                    <Text fontSize="12px" fontWeight="bold">
                      {data.name} :&nbsp;
                    </Text>
                    <Text fontSize="12px">
                      {humanizedCategoryValue.value}&nbsp;
                      {humanizedCategoryValue.unit}
                    </Text>
                    {data.value > 0 ? (
                      <Text fontSize="12px">
                        , soit {Math.round((data.value / co2TotalKg) * 100)}%
                      </Text>
                    ) : null}
                  </Box>
                </Box>
                  );
                })()
              ))
            : null}
        </Box>
      </ContentLayout>
      <Footer project={project} user={user} />
    </PageLayout>
  );
};

const sumLocFromChildren = (node: TreemapNode): number => {
  if (!node.children || node.children.length === 0) {
    return node.loc ?? 0;
  }
  return node.children.reduce(
    (acc, child) => acc + sumLocFromChildren(child),
    0,
  );
};

const treemapToPieData = (
  treemapData: TreemapData | undefined,
): Pie1DData[] => {
  if (!treemapData?.children?.length) {
    return [];
  }
  return treemapData.children.map(
    (child): Pie1DData => ({
      name: child.name,
      fill: child.color,
      value: sumLocFromChildren(child),
    }),
  );
};
